/** Sicherheit und Zugang (MP-E): Passkeys, angemeldete Geräte, Konto selbst löschen. */
import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button, Notice, Seitenzustand, StatusBadge, TextField } from "../ui";
import { Card } from "../ui/Layout";
import { api, ApiFehler, rufe } from "../api/client";
import { formatDatumZeit } from "@shared/zeit";
import { PasskeyKarte } from "../seiten/PasskeyKarte";
import { useT, useTx } from "../i18n";

interface Konto {
  sitzungen: { id: string; bereich: string; art: string; zuletztAt: string; erstelltAt: string; userAgent: string; aktuell: boolean }[];
}

function geraetName(ua: string): string {
  if (/iphone/i.test(ua)) return "iPhone";
  if (/ipad/i.test(ua)) return "iPad";
  if (/android/i.test(ua)) return "Android";
  if (/mac os/i.test(ua)) return "Mac";
  if (/windows/i.test(ua)) return "Windows";
  if (/linux/i.test(ua)) return "Linux";
  return "Gerät";
}
function browserName(ua: string): string {
  if (/edg\//i.test(ua)) return "Edge";
  if (/chrome|crios/i.test(ua)) return "Chrome";
  if (/firefox|fxios/i.test(ua)) return "Firefox";
  if (/safari/i.test(ua)) return "Safari";
  return "Browser";
}

export function Sicherheit() {
  const tx = useTx();
  const t = useT();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const konto = useQuery({ queryKey: ["konto"], queryFn: () => rufe<Konto>(api.api.konto.$get()) });
  const nach = () => void qc.invalidateQueries({ queryKey: ["konto"] });
  const beenden = useMutation({ mutationFn: (id: string) => rufe(api.api.konto.sitzungen[":id"].$delete({ param: { id } })), onSuccess: nach });
  const andere = useMutation({ mutationFn: () => rufe(api.api.konto.sitzungen["andere-beenden"].$post()), onSuccess: nach });
  const loeschInfo = useQuery({ queryKey: ["mieter", "konto-loeschen"], queryFn: () => rufe<{ geteilt: boolean; gruende: string[] }>(api.api.mieter.konto.loeschen.$get()) });
  const loeschen = useMutation({ mutationFn: () => rufe<{ art: string }>(api.api.mieter.konto.loeschen.$post({ json: { bestaetigt: true } })) });
  const [bestaetigt, setBestaetigt] = useState(false);

  async function kontoLoeschen() {
    if (!confirm("Wirklich löschen? Das lässt sich nicht rückgängig machen.")) return;
    try {
      await loeschen.mutateAsync();
      qc.clear();
      navigate("/anmelden?ziel=mieter", { replace: true });
    } catch {
      // Fehler wird unten angezeigt
    }
  }

  return (
    <div className="sc-col" style={{ gap: "var(--space-4)" }}>
      <div className="sc-row">
        <Link to="/mieter/profil" className="rg-btn rg-btn--quiet" aria-label={t("zurueck")}>‹</Link>
        <h1 className="title">{t("sicherheit.titel")}</h1>
      </div>
      <PasskeyKarte duzen />
      <StationPinKarte />
      <Seitenzustand query={konto} lines={3}>
        {(k) => (
          <Card title={`${t("sicherheit.geraete")} · ${k.sitzungen.length}`} actions={k.sitzungen.length > 1 ? <Button variant="secondary" icon="log-out" loading={andere.isPending} onClick={() => confirm("Alle anderen Geräte abmelden?") && andere.mutate()}>Alle anderen abmelden</Button> : undefined}>
            <p className="sc-muted sc-small">Hier siehst du, wo du angemeldet bist. Ein fremdes Gerät? Abmelden – und am besten einen Passkey anlegen.</p>
            <div className="sc-col" style={{ gap: 4 }}>
              {k.sitzungen.map((s) => (
                <div key={s.id} className="sc-spread" style={{ padding: "6px 0", borderBottom: "1px solid var(--line)" }}>
                  <span className="sc-col" style={{ gap: 0 }}>
                    <span className="sc-strong">{geraetName(s.userAgent)} · {browserName(s.userAgent)} {s.aktuell ? <StatusBadge status="paid">Dieses Gerät</StatusBadge> : null}</span>
                    <span className="sc-muted sc-small">{s.bereich === "mieter" ? "Mieterportal" : s.bereich} · zuletzt {formatDatumZeit(s.zuletztAt)} · seit {formatDatumZeit(s.erstelltAt)}</span>
                  </span>
                  {!s.aktuell ? <Button variant="quiet" icon="x" loading={beenden.isPending && beenden.variables === s.id} onClick={() => beenden.mutate(s.id)}>Abmelden</Button> : null}
                </div>
              ))}
            </div>
          </Card>
        )}
      </Seitenzustand>
      <Card title={t("sicherheit.loeschen")}>
        {loeschInfo.data?.geteilt ? (
          <p className="sc-muted sc-small">Du nutzt ein geteiltes Regal. Löschen entfernt nur deinen Zugang – das Regal und die Daten der Hauptmieterin bleiben.</p>
        ) : (
          <p className="sc-muted sc-small">Name, Kontakt, Adresse, IBAN, Fotos und dein Zugang werden gelöscht. Abrechnungen und Buchungen bleiben wegen der steuerlichen Aufbewahrungspflicht unter deiner Mieternummer beim Laden – ohne deinen Namen.</p>
        )}
        {loeschInfo.data?.gruende.length ? (
          <Notice tone="attention" title={tx("Noch nicht möglich")}>
            <ul style={{ margin: 0, paddingLeft: 18 }}>{loeschInfo.data.gruende.map((g) => <li key={g}>{g}</li>)}</ul>
          </Notice>
        ) : null}
        {loeschen.isError ? <Notice tone="error">{loeschen.error instanceof ApiFehler ? loeschen.error.message : "Löschen hat nicht geklappt."}</Notice> : null}
        <label className="sc-check"><input type="checkbox" checked={bestaetigt} onChange={(e) => setBestaetigt(e.target.checked)} disabled={!!loeschInfo.data?.gruende.length} /> Ich möchte mein Konto endgültig löschen.</label>
        <Button variant="danger" icon="trash-2" disabled={!bestaetigt || !!loeschInfo.data?.gruende.length} loading={loeschen.isPending} onClick={() => void kontoLoeschen()}>Konto löschen</Button>
      </Card>
    </div>
  );
}

/** E11.5: PIN für die Etiketten-Station im Laden. */
function StationPinKarte() {
  const tx = useTx();
  const [pin, setPin] = useState("");
  const [meldung, setMeldung] = useState<{ tone: "success" | "error"; text: string } | null>(null);
  const speichern = useMutation({ mutationFn: (p: string) => rufe(api.api.mieter["station-pin"].$put({ json: { pin: p } })) });
  return (
    <Card title={tx("PIN für die Etiketten-Station")}>
      <p className="sc-muted sc-small">Im Laden steht ein Tablet mit Drucker: Mieternummer und diese PIN eingeben, Etiketten drucken – ohne eigenes Handy. 4 bis 6 Ziffern, nicht dein Geburtsjahr.</p>
      {meldung ? <Notice tone={meldung.tone}>{meldung.text}</Notice> : null}
      <div className="sc-row" style={{ alignItems: "flex-end" }}>
        <TextField label="Neue PIN" type="password" value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" autoComplete="new-password" />
        <Button variant="secondary" icon="check" disabled={pin.length < 4} loading={speichern.isPending} onClick={() => speichern.mutate(pin, { onSuccess: () => { setPin(""); setMeldung({ tone: "success", text: "PIN gespeichert." }); }, onError: (e) => setMeldung({ tone: "error", text: e instanceof ApiFehler ? e.message : "Speichern hat nicht geklappt." }) })}>Speichern</Button>
      </div>
    </Card>
  );
}
